import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Download, Copy, Check, Pencil } from "lucide-react";
import { cn } from "@/lib/utils";
import type { CreativeItem } from "../../types/creatives.types";
import { dictionary, Language } from "@/utils/dictionary";
import { EditPanel } from "./EditPanel";
import { EditOverlay } from "./EditOverlay";
import { creativesService } from "../../services/creatives.service";
import toast from "react-hot-toast";
import { truncateProductName } from "../../utils/truncateProductName";

interface ViewCreativeModalProps {
  open: boolean;
  onClose: () => void;
  creatives: CreativeItem[];
  initialIndex: number;
  language: Language;
  onCreativeUpdated?: (id: number, newImageUrl: string) => void;
  initialEditMode?: boolean;
}

export function ViewCreativeModal({
  open,
  onClose,
  creatives,
  initialIndex,
  language,
  onCreativeUpdated,
  initialEditMode = false,
}: ViewCreativeModalProps) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [copiedCaption, setCopiedCaption] = useState(false);
  const [showEditPanel, setShowEditPanel] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editProgress, setEditProgress] = useState(0);
  const [replacedImages, setReplacedImages] = useState<Record<number, string>>({});
  const progressRef = useRef<ReturnType<typeof setInterval>>();

  useEffect(() => {
    if (open) {
      setCurrentIndex(initialIndex);
      setCopiedCaption(false);
      setShowEditPanel(initialEditMode);
      setReplacedImages({});
    }
  }, [open, initialIndex, initialEditMode]);

  // Preload images when modal opens so switching is instant (uses cache)
  useEffect(() => {
    if (open && creatives.length > 0) {
      creatives.forEach((c) => {
        if (c.image_url) {
          const img = new Image();
          img.src = c.image_url;
        }
      });
    }
  }, [open, creatives]);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") return handlePrevious();
      if (e.key === "ArrowRight") return handleNext();
      if (e.key !== "Escape") return;
      if (showEditPanel) return setShowEditPanel(false);
      onClose();
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, currentIndex, showEditPanel]);

  // Clean up progress interval on unmount
  useEffect(() => {
    return () => {
      if (progressRef.current) clearInterval(progressRef.current);
    };
  }, []);

  const getDisplayUrl = useCallback(
    (creative: CreativeItem) => {
      if (creative.id != null && replacedImages[creative.id]) {
        return replacedImages[creative.id];
      }
      return creative.image_url;
    },
    [replacedImages]
  );

  if (!open || creatives.length === 0) return null;

  const currentCreative = creatives[currentIndex];
  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex < creatives.length - 1;
  const displayedImageUrl = getDisplayUrl(currentCreative);

  const handlePrevious = () => {
    if (hasPrevious) {
      setCurrentIndex(currentIndex - 1);
      setCopiedCaption(false);
      setShowEditPanel(false);
    }
  };

  const handleNext = () => {
    if (hasNext) {
      setCurrentIndex(currentIndex + 1);
      setCopiedCaption(false);
      setShowEditPanel(false);
    }
  };

  const handleDownload = async () => {
    if (!displayedImageUrl) return;
    try {
      const response = await fetch(displayedImageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(currentCreative.name || currentCreative.product_name || "creativo").replace(/\s+/g, "-")}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading image:", error);
    }
  };

  const startProgress = () => {
    setEditProgress(0);
    if (progressRef.current) clearInterval(progressRef.current);
    const start = Date.now();
    const estimatedMs = 40000;
    progressRef.current = setInterval(() => {
      const elapsed = Date.now() - start;
      const pct = Math.min(90, (90 * Math.log(1 + elapsed / 1000)) / Math.log(1 + estimatedMs / 1000));
      setEditProgress(pct);
    }, 300);
  };

  const stopProgress = (complete: boolean) => {
    if (progressRef.current) clearInterval(progressRef.current);
    if (complete) {
      setEditProgress(100);
      setTimeout(() => setEditProgress(0), 400);
    } else {
      setEditProgress(0);
    }
  };

  const handleEditSubmit = async (instruction: string) => {
    if (!currentCreative?.id) return;
    setIsEditing(true);
    startProgress();
    try {
      const result = await creativesService.editCreativeImage(currentCreative.id, instruction);
      stopProgress(true);
      const newUrl = result.image_url;
      if (newUrl) {
        setReplacedImages((prev) => ({ ...prev, [currentCreative.id!]: newUrl }));
        onCreativeUpdated?.(currentCreative.id, newUrl);
        toast.success(t("image-edited-and-saved", "Image edited and saved"));
        setShowEditPanel(false);
      } else {
        throw new Error(t("error-editing-try-again", "Error editing. Try again."));
      }
    } catch (err) {
      stopProgress(false);
      toast.error(err instanceof Error ? err.message : t("error-editing-try-again", "Error editing. Try again."));
    } finally {
      setIsEditing(false);
    }
  };

  const t = (key: string, fallback: string) =>
    dictionary[key]?.[language as Language] ?? fallback;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.96 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
        className="relative max-w-4xl w-full mx-4 flex flex-col max-h-[95vh] bg-background rounded-2xl shadow-2xl border border-border/30"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border/40 flex-shrink-0">
          <span className="text-sm font-medium text-muted-foreground">
            {currentIndex + 1} {t("of", "de")} {creatives.length}
          </span>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-muted/50 transition-colors"
            aria-label={t("close", "Close")}
          >
            <X className="h-5 w-5 text-muted-foreground" />
          </button>
        </div>

        {/* Image area — flex-1 so it takes remaining space, min-h-0 so it can shrink */}
        <div className="relative flex-1 flex items-center justify-center p-4 sm:p-6 min-h-0 overflow-hidden bg-gradient-to-b from-muted/30 to-muted/10">
          {/* Navigation arrows */}
          {creatives.length > 1 && (
            <>
              <button
                onClick={handlePrevious}
                disabled={!hasPrevious}
                className={cn(
                  "absolute left-4 sm:left-6 z-10 p-3 rounded-full transition-all duration-200 shadow-sm",
                  "bg-background/80 backdrop-blur-sm border border-border/50 hover:bg-background text-foreground",
                  "disabled:opacity-30 disabled:pointer-events-none",
                  hasPrevious && "hover:scale-105"
                )}
                aria-label={t("previous", "Previous")}
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                onClick={handleNext}
                disabled={!hasNext}
                className={cn(
                  "absolute right-4 sm:right-6 z-10 p-3 rounded-full transition-all duration-200 shadow-sm",
                  "bg-background/80 backdrop-blur-sm border border-border/50 hover:bg-background text-foreground",
                  "disabled:opacity-30 disabled:pointer-events-none",
                  hasNext && "hover:scale-105"
                )}
                aria-label={t("next", "Next")}
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </>
          )}

          {/* Image with edit overlay */}
          <div className="relative flex items-center justify-center h-full w-full min-h-0">
            <div className="relative overflow-hidden rounded-lg shadow-2xl max-h-full">
              <img
                src={displayedImageUrl}
                alt={currentCreative.name || currentCreative.product_name || ""}
                className={cn(
                  "w-auto object-contain transition-all duration-300",
                  showEditPanel ? "max-h-[45vh]" : "max-h-[65vh]",
                  isEditing && "opacity-50"
                )}
              />
              <AnimatePresence>
                {isEditing && <EditOverlay progress={editProgress} />}
              </AnimatePresence>
            </div>
          </div>
        </div>

        {/* Footer — flex-shrink-0 so it's always visible */}
        <div className="border-t border-border/40 bg-background flex-shrink-0">
          {/* Product name — only in default view */}
          {!showEditPanel && (
            <div className="px-4 pt-3 pb-1">
              <h3 className="text-base font-semibold text-foreground truncate">
                {truncateProductName(currentCreative.name || currentCreative.product_name || t("no-name", "Sin nombre"))}
              </h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                {t("format", "Formato")}: {currentCreative.format}
              </p>
            </div>
          )}

          {/* Caption */}
          {currentCreative.caption && currentCreative.caption.trim().length > 30 && (
            <div className="px-4 py-3 border-b border-border/30">
              <div className="flex items-start justify-between gap-2 mb-1.5">
                <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                  {t("caption", "Caption")}
                </span>
                <button
                  onClick={async () => {
                    await navigator.clipboard.writeText(currentCreative.caption!);
                    setCopiedCaption(true);
                    setTimeout(() => setCopiedCaption(false), 2000);
                  }}
                  className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  {copiedCaption ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                  {copiedCaption ? t("copied", "Copiado") : t("copy", "Copiar")}
                </button>
              </div>
              <p className="text-sm text-foreground whitespace-pre-line bg-muted/30 rounded-lg p-3 leading-relaxed max-h-24 overflow-y-auto">
                {currentCreative.caption}
              </p>
            </div>
          )}

          {/* Edit panel */}
          <AnimatePresence>
            {showEditPanel && (
              <EditPanel
                onSubmit={handleEditSubmit}
                onCancel={() => setShowEditPanel(false)}
                isLoading={isEditing}
              />
            )}
          </AnimatePresence>

          {/* Thumbnails + Actions — always visible at the bottom */}
          <div className="flex items-center gap-3 px-4 py-3">
            {creatives.length > 1 && (
              <div className="flex gap-2.5 overflow-x-auto flex-1 py-1 px-0.5">
                {creatives.map((creative, index) => (
                  <motion.button
                    key={creative.id ?? index}
                    onClick={() => {
                      setCurrentIndex(index);
                      setShowEditPanel(false);
                      setCopiedCaption(false);
                    }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className={cn(
                      "w-12 h-12 rounded-md overflow-hidden flex-shrink-0 transition-all duration-200",
                      "bg-muted",
                      index === currentIndex
                        ? "ring-2 ring-primary ring-offset-1 ring-offset-background shadow-lg"
                        : "opacity-50 hover:opacity-100 border border-border/50"
                    )}
                  >
                    <img
                      src={getDisplayUrl(creative)}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  </motion.button>
                ))}
              </div>
            )}

            <div className={cn("flex gap-2 flex-shrink-0", creatives.length === 1 && "w-full")}>
              {!showEditPanel && (
                <button
                  onClick={() => setShowEditPanel(true)}
                  disabled={isEditing}
                  className={cn(
                    "flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors",
                    "border border-border/60 text-foreground hover:bg-muted/50",
                    "disabled:opacity-50 disabled:cursor-not-allowed",
                    creatives.length === 1 && "flex-1 justify-center"
                  )}
                >
                  <Pencil className="h-4 w-4" />
                  {t("edit", "Editar")}
                </button>
              )}
              <button
                onClick={handleDownload}
                className={cn(
                  "flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-white transition-colors",
                  "shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/25",
                  creatives.length === 1 && "flex-1 justify-center"
                )}
                style={{ background: "linear-gradient(to right, #4368E5, #4368E5)" }}
              >
                <Download className="h-4 w-4" />
                {t("download", "Descargar")}
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
